import Amirhossein from "./Images/amirhossein.jpg";
import Github from "./Images/github.png";
import Telegram from "./Images/telegram.png";
import Linkedin from "./Images/linkedin.png";
import Gmail from "./Images/gmail.png";
import { useState, useEffect } from "react";
import { ChevronDown, MapPin, Phone, Mail, ExternalLink } from "lucide-react";

function Navigation() {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.history.pushState(null, null, `#${sectionId}`);
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <nav className="w-full grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 p-4">
        {["Introduction", "Skills", "Experiences", "Contact Me"].map((item) => (
          <button
            key={item}
            className="group relative w-full h-16 rounded-xl bg-black/40 backdrop-blur-lg border border-white/20 hover:border-white/40 transition-all duration-500 overflow-hidden"
            onClick={() => scrollToSection(item)}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <span className="relative text-white font-medium text-sm md:text-base tracking-wide">
              {item}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function App() {
  const [showMore, setShowMore] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70 z-0" />

      <div
        className={`relative z-10 transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="pt-8 pb-16">
          <Navigation />

          <div className="text-center mt-12 mb-16 flex flex-col">
            <div className="relative inline-block mb-8">
              <div className="absolute -inset-4 bg-white/20 rounded-full blur-lg opacity-50 animate-pulse" />
              <h1 className="relative text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent tracking-tight">
                Amirhossein Alimardani Heravi
              </h1>
            </div>

            <div className="relative inline-block group mx-auto">
              <div className="absolute -inset-2 bg-white/10 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <a
                href="https://www.linkedin.com/in/amirhossein-alimardani-629a4532a/"
                target="_blank"
                rel="noreferrer"
                className="relative block"
              >
                <img
                  className="w-48 h-48 md:w-56 md:h-56 rounded-2xl object-cover border-4 border-white/20 shadow-2xl transform group-hover:scale-105 transition-transform duration-500"
                  src={Amirhossein}
                  alt="Amirhossein Alimardani"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </a>
            </div>

            <div className="mt-8 max-w-2xl mx-auto px-4">
              <blockquote className="text-xl md:text-2xl text-white/90 font-light italic backdrop-blur-lg rounded-2xl p-6 border border-white/10 bg-black/30">
                "Strive not to be a success, but rather to be{" "}
                <span className="font-bold text-white">of value.</span>"
              </blockquote>
            </div>
          </div>
        </div>

        <section id="Introduction" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3">
                <div className="w-12 h-px bg-white/40"></div>
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  Introduction
                </h2>
                <div className="w-12 h-px bg-white/40"></div>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-4 bg-white/5 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 md:p-10">
                <p
                  className={`text-white/90 text-lg leading-relaxed transition-all duration-500 ${
                    showMore ? "line-clamp-6 md:h-[10rem]" : "md:h-[22rem]"
                  }`}
                >
                  My name is Amirhossein Alimardani Heravi, and I hold the
                  position of Lead Instructor and Curriculum Developer at
                  PythonTeek. As a graduate of Computer Science from Ferdowsi
                  University of Mashhad (FUM), I have dedicated my career to
                  demystifying the power of Python programming and empowering
                  the next generation of developers. With over 5 years of
                  experience in the field, I specialize in architecting and
                  delivering comprehensive educational programs in key modern
                  domains, including Artificial Intelligence and Machine
                  Learning. My work is driven by a passion for translating
                  complex computational concepts into accessible, engaging, and
                  practical learning experiences. I have a proven track record
                  of designing curricula that not only teach syntax but also
                  foster critical problem-solving skills, enabling students to
                  build robust applications and AI models. My true passion lies
                  in mentoring aspiring coders and engaging with the
                  cutting-edge challenges of the tech landscape, where I can
                  leverage my expertise to bridge the gap between theoretical
                  computer science and real-world application.
                </p>

                <button
                  className="mt-6 flex items-center gap-2 text-white/70 hover:text-white transition-colors duration-300 group/btn"
                  onClick={() => setShowMore(!showMore)}
                >
                  <span className="font-medium">
                    Show {showMore ? "more" : "less"}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`transform transition-transform duration-300 ${
                      showMore ? "" : "rotate-180"
                    } group-hover/btn:scale-110`}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section id="Skills" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3">
                <div className="w-12 h-px bg-white/40"></div>
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  Skills
                </h2>
                <div className="w-12 h-px bg-white/40"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="group">
                <div className="h-full bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all duration-500">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <div className="w-2 h-8 bg-blue-500 rounded-full"></div>
                    Technical Skills
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xl font-semibold text-white mb-4">
                        Programming
                      </h4>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          {
                            skill: "Python",
                            level: "Advanced",
                            color: "from-blue-500 to-cyan-500",
                          },
                          {
                            skill: "Java",
                            level: "Intermediate",
                            color: "from-red-500 to-orange-500",
                          },
                          {
                            skill: "MATLAB",
                            level: "Intermediate",
                            color: "from-orange-500 to-yellow-500",
                          },
                          {
                            skill: "R",
                            level: "Intermediate",
                            color: "from-blue-600 to-purple-600",
                          },
                          {
                            skill: "SQL",
                            level: "Intermediate",
                            color: "from-green-500 to-emerald-500",
                          },
                        ].map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300"
                          >
                            <span className="text-white font-medium">
                              {item.skill}
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${item.color} text-white`}
                            >
                              {item.level}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold text-white mb-4">
                        Tools & Libraries
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {[
                          "Microsoft Office Suite",
                          "Pandas",
                          "NumPy",
                          "Scikit-learn",
                        ].map((tool, index) => (
                          <span
                            key={index}
                            className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors duration-300 border border-white/10"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="h-full bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all duration-500">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                    <div className="w-2 h-8 bg-purple-500 rounded-full"></div>
                    Soft Skills
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      {
                        skill: "Leadership",
                        color: "bg-blue-500/20 border-blue-500/40",
                      },
                      {
                        skill: "Collaboration",
                        color: "bg-purple-500/20 border-purple-500/40",
                      },
                      {
                        skill: "Time Management",
                        color: "bg-orange-500/20 border-orange-500/40",
                      },
                      {
                        skill: "Problem Solving",
                        color: "bg-red-500/20 border-red-500/40",
                      },
                      {
                        skill: "Adaptability",
                        color: "bg-pink-500/20 border-pink-500/40",
                      },
                      {
                        skill: "Mentoring",
                        color: "bg-teal-500/20 border-teal-500/40",
                      },
                      {
                        skill: "Teamwork",
                        color: "bg-cyan-500/20 border-cyan-500/40",
                      },
                      {
                        skill: "Communication",
                        color: "bg-green-500/20 border-green-500/40",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-xl border-2 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg ${item.color}`}
                      >
                        <span className="text-white font-medium text-sm">
                          {item.skill}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="Experiences" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3">
                <div className="w-12 h-px bg-white/40"></div>
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  Experiences
                </h2>
                <div className="w-12 h-px bg-white/40"></div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="group">
                <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all duration-500">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        Instructor
                      </h3>
                      <h4 className="text-xl text-white/80 mt-1">
                        PythonTeek | Feb 2025 - Present
                      </h4>
                    </div>
                    <div className="mt-2 md:mt-0 px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-full">
                      <span className="text-green-400 font-semibold text-sm">
                        Current Role
                      </span>
                    </div>
                  </div>
                  <ul className="text-white/90 space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        AI Instructor teaching machine learning tools and
                        concepts
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        Data Science Instructor focusing on data manipulation
                        and analysis techniques
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        Game Development Instructor teaching game design
                        principles
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        General Python Programming Instructor for foundational
                        programming skills
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="group">
                <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all duration-500">
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Teaching Assistant Roles
                  </h3>

                  <div className="space-y-6">
                    {[
                      {
                        title: "Data Structures & Algorithms (2x)",
                        period:
                          "Ferdowsi University of Mashhad | Sept 2023 - Feb 2024 & Feb 2025 - Present",
                        responsibilities: [
                          "Designed and analyzed algorithms for practical student projects",
                          "Provided comprehensive problem sets and evaluated assignments",
                          "Conducted problem-solving sessions for junior students",
                          "Assigned and analyzed student tasks and projects",
                        ],
                      },
                      {
                        title: "Theory of Computation (3x)",
                        period:
                          "Ferdowsi University of Mashhad | Sept 2023 - Jul 2024 & Sept 2024 - Feb 2025",
                        responsibilities: [
                          "Conducted problem-solving sessions for junior students",
                          "Answered questions and provided guidance on project definitions",
                        ],
                      },
                      {
                        title: "Design & Analysis of Algorithms",
                        period:
                          "Ferdowsi University of Mashhad | Sept 2024 - Jan 2025",
                        responsibilities: [
                          "Designed and analyzed algorithms for practical student projects",
                          "Provided problem sets and evaluated assignments",
                        ],
                      },
                      {
                        title: "Principles of Software Design",
                        period:
                          "Ferdowsi University of Mashhad | Sept 2024 - Feb 2025",
                        responsibilities: [
                          "Assisted in project-based learning by designing an online shop as sample software",
                          "Guided students in software project analysis and implementation",
                        ],
                      },
                    ].map((role, index) => (
                      <div
                        key={index}
                        className="border-l-2 border-white/20 pl-6 ml-2"
                      >
                        <h4 className="text-xl font-semibold text-white">
                          {role.title}
                        </h4>
                        <p className="text-white/70 text-sm mt-1 mb-3">
                          {role.period}
                        </p>
                        <ul className="text-white/90 space-y-2">
                          {role.responsibilities.map((resp, respIndex) => (
                            <li
                              key={respIndex}
                              className="flex items-start gap-3 text-sm"
                            >
                              <div className="w-1.5 h-1.5 bg-white rounded-full mt-1.5 flex-shrink-0" />
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 hover:border-white/20 transition-all duration-500">
                  <h3 className="text-2xl font-bold text-white">
                    Research Assistant
                  </h3>
                  <h4 className="text-xl text-white/80 mt-1 mb-4">
                    Ferdowsi University of Mashhad | Jun 2024 - Sept 2024
                  </h4>
                  <ul className="text-white/90 space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        Developed software quality metrics toolkit under Prof.
                        Haniyeh Khosravi
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        Conducted comprehensive literature review in software
                        engineering
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0" />
                      <span>
                        Collaborated on developing and implementing research
                        methodology
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="Contact Me" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3">
                <div className="w-12 h-px bg-white/40"></div>
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  Contact Me
                </h2>
                <div className="w-12 h-px bg-white/40"></div>
              </div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-8 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Connect With Me
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      {
                        icon: Telegram,
                        href: "https://t.me/Amirhosseina18",
                        label: "Telegram",
                      },
                      {
                        icon: Github,
                        href: "https://github.com/amirswift",
                        label: "GitHub",
                      },
                      {
                        icon: Gmail,
                        href: "mailto:Amiralimardani.h@gmail.com",
                        label: "Email",
                      },
                      {
                        icon: Linkedin,
                        href: "https://www.linkedin.com/in/amirhossein-alimardani-629a4532a/",
                        label: "LinkedIn",
                      },
                    ].map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noreferrer"
                        className="group relative p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/30 transition-all duration-500 hover:scale-105"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div className="relative">
                            <div className="absolute inset-0 bg-white/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <img
                              className="relative w-10 h-10 bg-white rounded-full p-1.5"
                              src={social.icon}
                              alt={social.label}
                            />
                          </div>
                          <span className="text-white font-medium text-sm">
                            {social.label}
                          </span>
                        </div>
                        <ExternalLink
                          size={16}
                          className="absolute top-3 right-3 text-white/50 group-hover:text-white transition-colors duration-300"
                        />
                      </a>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Get In Touch
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300">
                      <MapPin className="w-6 h-6 text-white/70 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white">Address</h4>
                        <p className="text-white/80 text-sm mt-1">
                          Azadi Square, Ferdowsi University of Mashhad
                          <br />
                          Mashhad, Iran
                          <br />
                          Phone Number: 051 3880 5000
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300">
                      <Phone className="w-6 h-6 text-white/70 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white">Phone</h4>
                        <p className="text-white/80 text-sm mt-1">
                          +98 903-283-2638
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300">
                      <Mail className="w-6 h-6 text-white/70 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white">Email</h4>
                        <p className="text-white/80 text-sm mt-1">
                          Amiralimardani.h@gmail.com
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white/60 text-sm">
              © 2025 Amirhossein Alimardani Heravi. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
